/**
 * Recovery for failed dynamic imports (`vite:preloadError` and React.lazy).
 *
 * A chunk request fails for two very different reasons:
 * - the network dropped it (flaky mobile data, a proxy or antivirus in the way) — the
 *   file is still on the CDN, so simply asking again usually works;
 * - the hashed file is gone because the tab is still running a previous build, or the
 *   SPA fallback answers a missing `/assets/*.js` with index.html, so the browser gets
 *   HTML where it expected a module — only a reload onto the current index.html helps.
 *
 * So imports are retried in place first (`importWithRetry`, used by `lazyWithRetry`),
 * the page is reloaded only when that fails too, and Sentry only hears about it when
 * the reload budget is spent and the user is left looking at the error screen.
 *
 * Never call `event.preventDefault()` on `vite:preloadError`: vite's preload helper
 * rethrows only when the event was not cancelled, so suppressing it makes the import
 * resolve with `undefined`, and React.lazy then throws "can't access property
 * default, _result is undefined" — an unrecoverable render crash instead of a plain
 * chunk error the boundary can handle.
 */
import * as Sentry from '@sentry/react'

const RETRIES_KEY = 'vite_preload_retries'
const LAST_RELOAD_KEY = 'vite_last_reload'

/** Reloads allowed per session before giving up and showing the error screen */
const MAX_RETRIES = 3
/** Minimum gap between reloads, so a permanently missing chunk cannot spin */
const COOL_DOWN_MS = 10 * 1000
/** How long the app must stay up before the retry budget is handed back */
const STABLE_SESSION_MS = 30 * 1000

/** In-place import attempts after the first failure, before falling back to a reload */
const IMPORT_RETRIES = 2
/** Delay before the first in-place retry; doubles on each further attempt */
const IMPORT_RETRY_DELAY_MS = 1000

const CHUNK_ERROR_PATTERNS = [
  /ChunkLoadError/i,
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /Unable to preload CSS/i,
  // MIME mismatch — index.html served in place of a deleted chunk
  /expected a javascript(-or-wasm)? module/i,
  /disallowed MIME type/i,
  // vite swallowed the preload error, so React.lazy resolved with `undefined`
  /_result is undefined/i,
  /reading '?default'?/i,
]

/** The chunk URL browsers put in the message, e.g. `…module: https://x/assets/A-1b2.js` */
const MODULE_URL_PATTERN = /(?:https?:\/\/[^\s'"]+|\/[^\s'"]+)\.m?js\b/

/** Errors an `importWithRetry` call has taken over, so `vite:preloadError` leaves them be */
const retryManagedErrors = new WeakSet<object>()
/** Errors already sent to Sentry, so the event listener and the boundary report once */
const reportedErrors = new WeakSet<object>()

export const isChunkLoadError = (error: any): boolean => {
  if (!error) return false

  const text = `${error?.name || ''} ${error?.message || ''}`
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(text))
}

const markRetryManaged = (error: unknown) => {
  if (error && typeof error === 'object') retryManagedErrors.add(error)
}

export const isRetryManaged = (error: unknown): boolean =>
  !!error && typeof error === 'object' && retryManagedErrors.has(error)

/** Absolute URL of the chunk named in a browser's import error, or null if it names none */
export const failedModuleUrl = (error: any): string | null => {
  const match = String(error?.message || '').match(MODULE_URL_PATTERN)
  if (!match) return null
  try {
    return new URL(match[0], window.location.href).href
  } catch {
    return null
  }
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Runs a dynamic import, retrying it in place when the chunk fails to load.
 *
 * Retries import the failed URL with a cache-busting query: Chrome remembers a failed
 * module URL for the life of the document and fails it again without asking the
 * network, so calling the factory again is useless there. The chunk's own imports keep
 * their normal URLs, so shared modules are not instantiated twice, and its CSS was
 * already requested by the first, preloading attempt. When the message carries no URL
 * the factory is called again instead.
 *
 * Non-chunk errors (a module that throws while evaluating) are rethrown untouched.
 */
export const importWithRetry = async <T>(
  factory: () => Promise<T>,
  retries: number = IMPORT_RETRIES
): Promise<T> => {
  try {
    return await factory()
  } catch (error) {
    if (!isChunkLoadError(error) || retries <= 0) throw error
    markRetryManaged(error)

    const url = failedModuleUrl(error)
    for (let attempt = 1; attempt <= retries; attempt++) {
      await wait(IMPORT_RETRY_DELAY_MS * 2 ** (attempt - 1))
      try {
        return url ? await import(/* @vite-ignore */ `${url}?retry=${Date.now()}`) : await factory()
      } catch (retryError) {
        if (!isChunkLoadError(retryError)) throw retryError
        markRetryManaged(retryError)
      }
    }

    console.warn(`[Chunk Reload] Import still failing after ${retries} retries`, error)
    throw error
  }
}

/**
 * Tells Sentry a chunk could not be loaded and the user was left on the error screen.
 * One fingerprint for all of them: the hashed file name in the message otherwise opens
 * a new issue per tenant, chunk and build.
 */
export const reportChunkLoadFailure = (error: unknown, source: string) => {
  if (error && typeof error === 'object') {
    if (reportedErrors.has(error)) return
    reportedErrors.add(error)
  }
  Sentry.captureException(error, {
    fingerprint: ['chunk-load-error'],
    tags: {chunk_load_source: source},
    extra: {reloadAttempts: Number(sessionStorage.getItem(RETRIES_KEY) || 0)},
  })
}

/**
 * Reloads the page to pick up the current index.html, within a per-session budget.
 * Returns whether a reload was actually triggered; when it was not, the failure is
 * reported, because nothing else is going to recover from it.
 */
export const reloadForChunkError = (source: string, error?: unknown): boolean => {
  const retries = Number(sessionStorage.getItem(RETRIES_KEY) || 0)
  const lastReload = Number(sessionStorage.getItem(LAST_RELOAD_KEY) || 0)
  const now = Date.now()

  if (retries >= MAX_RETRIES) {
    console.error(`[Chunk Reload] Preload failed too many times (${source}) — aborting reloads.`)
    reportChunkLoadFailure(error, source)
    return false
  }

  if (now - lastReload < COOL_DOWN_MS) {
    console.warn(`[Chunk Reload] Cool down active (${source}) — skipping reload.`)
    reportChunkLoadFailure(error, source)
    return false
  }

  sessionStorage.setItem(RETRIES_KEY, `${retries + 1}`)
  sessionStorage.setItem(LAST_RELOAD_KEY, `${now}`)

  console.warn(
    `[Chunk Reload] Chunk error detected (${source}) — retrying (${retries + 1}/${MAX_RETRIES})`
  )
  window.location.reload()
  return true
}

/**
 * `vite:preloadError` listener. Imports going through `importWithRetry` are left to
 * it (and to ChunkErrorBoundary after that); any other dynamic import that fails —
 * e.g. one awaited in an event handler — reloads straight away, as before.
 *
 * vite dispatches the event and then rethrows, so the decision waits a macrotask: by
 * then `importWithRetry` has caught the same error object and marked it.
 */
export const handleVitePreloadError = (event: Event) => {
  const error = (event as Event & {payload?: unknown}).payload
  setTimeout(() => {
    if (!isRetryManaged(error)) reloadForChunkError('vite:preloadError', error)
  }, 0)
}

/**
 * Hands the retry budget back once the app has stayed up for a while. Clearing it on
 * every `load` made MAX_RETRIES unreachable, because each reload reset the counter.
 */
export const releaseChunkReloadBudgetWhenStable = () => {
  window.setTimeout(() => {
    sessionStorage.removeItem(RETRIES_KEY)
    sessionStorage.removeItem(LAST_RELOAD_KEY)
  }, STABLE_SESSION_MS)
}
